import { GenericFormData } from './types'
import { useEffect, useState } from 'react'
import { areFormDataEqual } from './utils'

export const useSettingsPopupState = <FormData extends GenericFormData = GenericFormData>(
  getInitialFormData: () => FormData,
  // Reset form whenever one of these variables changes
  // Either use this deps array, or use `resetFormData` callback returned from this hook if needed
  // We usually need to reset the state when openSection changes, so state doesn't carry over between form and json
  resetFormDependencyArray: any[] = []
) => {
  const [formData, setFormData] = useState(getInitialFormData)
  const [initialFormData, setInitialFormData] = useState(getInitialFormData)
  const [isTouched, setIsTouched] = useState(false)

  const resetFormData = (to: FormData) => {
    setFormData(to)
    setInitialFormData(to)
    setIsTouched(false)
  }

  const onFormDataChange: OnFormDataChange<FormData> = (newFormData, isInitialPopulation) => {
    if (isInitialPopulation) {
      // When value does not adhere to schema, RJSF fires onChange internally when it populates
      // the form with default values. We want to treat this as the initial state, rather than a change
      resetFormData(newFormData)
      return
    }

    setFormData(newFormData)
    // only run areFormDataEqual until touched for the first time:)
    setIsTouched(prev => prev || !areFormDataEqual(initialFormData, newFormData))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => resetFormData(getInitialFormData()), resetFormDependencyArray)

  return { formData, initialFormData, isTouched, onFormDataChange, resetFormData }
}

export type OnFormDataChange<FormData extends GenericFormData = GenericFormData> = (
  newFormData: FormData,
  isInitialPopulation: boolean
) => void
